import { useState, useMemo, useRef } from 'react';
import {
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  Button,
  Badge,
  Banner,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  EmptyState,
  Search,
  Filter,
  FilterGroup,
  Pagination,
  CircularLoader,
  GroupIcon,
  PlusIcon,
  FilterIcon,
  TrashIcon,
  EditIcon,
  MoreVerticalIcon,
  ChevronRightIcon,
  ContextualMenu,
  ContextualMenuItem,
  Divider,
  Modal,
  ModalContent,
  Input,
  Control,
  Label,
  TextArea,
  useDocumentTitle,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import type { FilterOptionCheckboxValue, FilterOptionDateRangeValue, FilterOptionNumberRangeValue } from '@forge/common';
import { useKeycloakUsers } from '@/hooks/useKeycloakUsers';
import { useKeycloakGroups } from '@/hooks/useKeycloakGroups';
import { useGroupMembers } from '@/hooks/useGroupMembers';
import { keycloakApi } from '@/api/keycloak';
import UserDetailPanel from './components/UserDetailPanel';
import InviteUserDialog from './components/InviteUserDialog';
import CreateGroupDialog from './components/CreateGroupDialog';
import AddMemberDialog from './components/AddMemberDialog';
import MockDataBanner from './components/MockDataBanner';
import JoinRequestsTab from './components/JoinRequestsTab';
import { useApiQuery } from '@/hooks/useApiQuery';
import { listPendingJoinRequests } from '@/api/groups';
import type { PendingJoinRequest } from '@/api/groups';
import { getAvatarColor, getInitials, toTitleCase } from '@/utils/userDisplay';

type ActiveTab = 'users' | 'groups' | 'requests';

interface AccessUser {
  id: string;
  objectId: string;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  mfaEnabled: boolean;
  loginMethod: string;
  userType: string;
  status: string;
  roles: string[];
  groups: string[];
  createdAt: string;
  lastLogin: string | undefined;
}

const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const AccessManagementPage = () => {
  useDocumentTitle('Access Management — OneRing Portal');

  const [activeTab, setActiveTab] = useState<ActiveTab>('users');

  // Pending join-requests count (for the Requests tab badge)
  const pendingRequestsQuery = useApiQuery<{ requests: PendingJoinRequest[] }>({
    queryFn: (signal) => listPendingJoinRequests(signal),
    mockData: { requests: [] },
  });
  const pendingRequestCount = pendingRequestsQuery.data?.requests.length ?? 0;

  // Data hooks — real API with mock fallback
  const {
    users,
    isLoading: usersLoading,
    isUsingMockData: usersUsingMock,
    error: usersError,
    reload: reloadUsers,
  } = useKeycloakUsers();

  const {
    groups,
    isLoading: groupsLoading,
    isUsingMockData: groupsUsingMock,
    error: groupsError,
    reload: reloadGroups,
  } = useKeycloakGroups();

  const groupIds = useMemo(() => groups.map((g) => g.id), [groups]);
  const {
    membersByGroup,
    countByGroup,
    reload: reloadMembers,
  } = useGroupMembers(groupIds);

  const isLoading = usersLoading || groupsLoading;
  const isUsingMockData = usersUsingMock || groupsUsingMock;
  const loadError = usersError ?? groupsError;

  // Users tab state
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState<string[]>([]);
  const [userGroupFilter, setUserGroupFilter] = useState<string[]>([]);
  const [userMfaFilter, setUserMfaFilter] = useState<string[]>([]);
  const [userTypeFilter, setUserTypeFilter] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AccessUser | undefined>(undefined);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(10);

  // Groups tab state
  const [groupSearch, setGroupSearch] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [showDeleteGroupConfirm, setShowDeleteGroupConfirm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(false);
  const [groupNameDraft, setGroupNameDraft] = useState('');
  const [groupDescDraft, setGroupDescDraft] = useState('');
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [groupsPage, setGroupsPage] = useState(1);
  const [membersPage, setMembersPage] = useState(1);
  const groupsPageSize = 8;
  const membersPageSize = 8;
  const groupActionsRef = useRef<HTMLButtonElement>(null);

  // Reverse index: which group ids does each user belong to. Built from the
  // per-group member lists since Keycloak's user list omits group membership.
  const groupIdsByUser = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const [groupId, members] of Object.entries(membersByGroup)) {
      for (const m of members) {
        (map[m.userId] ??= []).push(groupId);
      }
    }
    return map;
  }, [membersByGroup]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        !userSearch ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.username.toLowerCase().includes(userSearch.toLowerCase());

      const matchesStatus =
        userStatusFilter.length === 0 || userStatusFilter.includes(user.status);

      const userGroups = groupIdsByUser[user.id] ?? [];
      const matchesGroup =
        userGroupFilter.length === 0 || userGroups.some((g) => userGroupFilter.includes(g));

      const matchesMfa =
        userMfaFilter.length === 0 ||
        (userMfaFilter.includes('enabled') && user.mfaEnabled) ||
        (userMfaFilter.includes('disabled') && !user.mfaEnabled);

      const matchesType =
        userTypeFilter.length === 0 || userTypeFilter.includes(user.userType);

      return matchesSearch && matchesStatus && matchesGroup && matchesMfa && matchesType;
    });
  }, [users, userSearch, userStatusFilter, userGroupFilter, userMfaFilter, userTypeFilter, groupIdsByUser]);

  // Paginated users
  const usersTotalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPageSize));
  const paginatedUsers = useMemo(() => {
    const start = (usersPage - 1) * usersPageSize;
    return filteredUsers.slice(start, start + usersPageSize);
  }, [filteredUsers, usersPage, usersPageSize]);

  // Filtered groups
  const filteredGroups = useMemo(() => {
    return groups.filter(
      (group) =>
        !groupSearch ||
        group.name.toLowerCase().includes(groupSearch.toLowerCase()) ||
        group.description.toLowerCase().includes(groupSearch.toLowerCase())
    );
  }, [groups, groupSearch]);

  const groupsTotalPages = Math.max(1, Math.ceil(filteredGroups.length / groupsPageSize));
  const paginatedGroups = useMemo(() => {
    const start = (groupsPage - 1) * groupsPageSize;
    return filteredGroups.slice(start, start + groupsPageSize);
  }, [filteredGroups, groupsPage]);

  // Selected group details
  const selectedGroup = useMemo(() => {
    return groups.find((g) => g.id === selectedGroupId);
  }, [groups, selectedGroupId]);

  // Members come directly from Keycloak per group (the user list endpoint does
  // not expose group membership, so it cannot be derived from `users`).
  const memberCountByGroup = countByGroup;

  const selectedGroupMembers = useMemo(() => {
    if (!selectedGroupId) return [];
    return membersByGroup[selectedGroupId] ?? [];
  }, [selectedGroupId, membersByGroup]);

  const membersTotalPages = Math.max(
    1,
    Math.ceil(selectedGroupMembers.length / membersPageSize),
  );
  const effectiveMembersPage = Math.min(membersPage, membersTotalPages);
  const paginatedMembers = useMemo(() => {
    const start = (effectiveMembersPage - 1) * membersPageSize;
    return selectedGroupMembers.slice(start, start + membersPageSize);
  }, [selectedGroupMembers, effectiveMembersPage]);

  // Handlers
  const handleRemoveGroup = (_userId: string, _groupId: string) => {
    // Reload both users and groups to reflect the change from the API
    reloadUsers();
    reloadGroups();
    if (selectedUser?.id === _userId) {
      setSelectedUser(undefined);
    }
  };

  const handleSuspendUser = (_userId: string) => {
    reloadUsers();
    setSelectedUser(undefined);
  };

  const handleInviteSuccess = () => {
    setShowInviteDialog(false);
    reloadUsers();
    reloadGroups();
  };

  const handleCreateGroupSuccess = () => {
    setShowCreateGroupDialog(false);
    reloadGroups();
  };

  const handleRemoveMemberFromGroup = async (userId: string) => {
    if (!selectedGroupId) return;
    const ok = await removeMember({ groupId: selectedGroupId, userId });
    if (!ok) return;
    reloadMembers();
    reloadUsers();
  };

  const { mutate: deleteGroup, isSubmitting: isDeletingGroup } = useApiMutation<
    string,
    boolean
  >({
    mutationFn: async (groupId) => {
      await keycloakApi.deleteGroup(groupId);
      return true;
    },
    successMessage: 'Group deleted',
    errorMessage: 'Failed to delete group',
  });

  const { mutate: editGroup, isSubmitting: isEditingGroup } = useApiMutation<
    { groupId: string; name: string; description: string },
    boolean
  >({
    mutationFn: async ({ groupId, name, description }) => {
      await keycloakApi.updateGroup(groupId, name, description);
      return true;
    },
    successMessage: 'Group updated',
    errorMessage: 'Failed to update group',
  });

  const { mutate: addMembers, isSubmitting: isAddingMember } = useApiMutation<
    { groupId: string; userIds: string[] },
    boolean
  >({
    mutationFn: async ({ groupId, userIds }) => {
      await Promise.all(
        userIds.map((userId) => keycloakApi.addUserToGroup(groupId, userId)),
      );
      return true;
    },
    successMessage: 'Members added to group',
    errorMessage: 'Failed to add members',
  });

  const { mutate: removeMember } = useApiMutation<
    { groupId: string; userId: string },
    boolean
  >({
    mutationFn: async ({ groupId, userId }) => {
      await keycloakApi.removeUserFromGroup(groupId, userId);
      return true;
    },
    successMessage: 'Member removed from group',
    errorMessage: 'Failed to remove member',
  });

  const handleDeleteGroup = async () => {
    if (!selectedGroupId) return;
    const ok = await deleteGroup(selectedGroupId);
    if (!ok) return;
    setShowDeleteGroupConfirm(false);
    setSelectedGroupId(undefined);
    reloadGroups();
    reloadMembers();
  };

  const handleEditGroup = async () => {
    if (!selectedGroupId || !groupNameDraft.trim()) return;
    const ok = await editGroup({
      groupId: selectedGroupId,
      name: groupNameDraft.trim(),
      description: groupDescDraft.trim(),
    });
    if (!ok) return;
    setEditingGroup(false);
    reloadGroups();
  };

  const handleAddMembers = async (userIds: string[]) => {
    if (!selectedGroupId || userIds.length === 0) return;
    const ok = await addMembers({ groupId: selectedGroupId, userIds });
    if (!ok) return;
    setShowAddMemberDialog(false);
    reloadUsers();
    reloadMembers();
  };

  const getGroupName = (groupId: string) => {
    return groups.find((g) => g.id === groupId)?.name || groupId;
  };

  const handleFilterChange = (values: Record<string, FilterOptionCheckboxValue[] | FilterOptionDateRangeValue | FilterOptionNumberRangeValue>) => {
    const statuses = ((values['user-status'] || []) as FilterOptionCheckboxValue[]).map((v) => String(v.value));
    const groupIds = ((values['user-groups'] || []) as FilterOptionCheckboxValue[]).map((v) => String(v.value));
    const mfa = ((values['user-mfa'] || []) as FilterOptionCheckboxValue[]).map((v) => String(v.value));
    const types = ((values['user-type'] || []) as FilterOptionCheckboxValue[]).map((v) => String(v.value));
    setUserStatusFilter(statuses);
    setUserGroupFilter(groupIds);
    setUserMfaFilter(mfa);
    setUserTypeFilter(types);
    setUsersPage(1);
    setFilterOpen(false);
  };

  const handleFilterClearAll = () => {
    setUserStatusFilter([]);
    setUserGroupFilter([]);
    setUserMfaFilter([]);
    setUserTypeFilter([]);
    setUsersPage(1);
    setFilterOpen(false);
  };

  const activeFilterCount = userStatusFilter.length + userGroupFilter.length + userMfaFilter.length + userTypeFilter.length;

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-1">Access Management</h2>
      <p className="text-sm text-color-tertiary mb-4">Manage users, groups, and platform access permissions</p>

      <div className="mb-4">
        {isUsingMockData && <MockDataBanner featureName="Access Management" />}
      </div>

      {isLoading ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3">
          <CircularLoader size="large" />
          <p className="text-sm text-color-tertiary">Loading access management data...</p>
        </div>
      ) : loadError ? (
        <div className="flex flex-col gap-4">
          <Banner
            variant="inline"
            type="error"
            title="Failed to load access management data"
            description={loadError}
            canClose={false}
          />
          <div>
            <Button
              variant="secondary"
              onClick={() => {
                reloadUsers();
                reloadGroups();
              }}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : (
      <>
      <Tabs value={activeTab} onChange={(id) => setActiveTab(id as ActiveTab)}>
        <Tab id="users" active={activeTab === 'users'}>
          {`Users (${users.length})`}
        </Tab>
        <Tab id="groups" active={activeTab === 'groups'}>
          {`Groups (${groups.length})`}
        </Tab>
        <Tab id="requests" active={activeTab === 'requests'}>
          {pendingRequestCount > 0
            ? `Join Requests (${pendingRequestCount})`
            : 'Join Requests'}
        </Tab>
      </Tabs>

      {activeTab === 'users' && (
        <div className="flex flex-col gap-4 mt-4">
          {/* Users Card with Header containing Search and Filter */}
          <Card>
            <CardHeader
              title="Users"
              subtitle={`${filteredUsers.length} user${filteredUsers.length !== 1 ? 's' : ''}`}
              slotRight={
                <div className="flex items-center gap-3">
                  <Search
                    id="user-search"
                    placeholder="Search users..."
                    onChange={(e) => setUserSearch(e.target.value)}
                  />
                  <Button
                    variant="tertiary"
                    icon={<FilterIcon />}
                    onClick={() => setFilterOpen(true)}
                  >
                    Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                  </Button>
                  <Button
                    variant="primary"
                    icon={<PlusIcon />}
                    onClick={() => setShowInviteDialog(true)}
                  >
                    Invite User
                  </Button>
                </div>
              }
            />
            <CardContent>
              {filteredUsers.length === 0 ? (
                <EmptyState icon={<GroupIcon />} description="No users found" />
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Groups</TableCell>
                        <TableCell>MFA</TableCell>
                        <TableCell>Last Login</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedUsers.map((user) => (
                        <TableRow
                          key={user.id}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                          onClick={() => setSelectedUser(user)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar
                                text={getInitials(user.firstName, user.lastName, user.email)}
                                size="sm"
                                aria-label={`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email}
                                style={getAvatarColor(user.id || user.email)}
                              />
                              <div>
                                <p className="font-medium">
                                  {toTitleCase(user.firstName)} {toTitleCase(user.lastName)}
                                </p>
                                <p className="text-xs text-color-tertiary">{user.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge color={user.userType === 'internal' ? 'blue' : 'gray'}>
                              {capitalizeFirst(user.userType)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              {(groupIdsByUser[user.id] ?? []).length > 0 ? (
                                (groupIdsByUser[user.id] ?? []).map((groupId) => (
                                  <span key={groupId} className="text-sm">
                                    {getGroupName(groupId)}
                                  </span>
                                ))
                              ) : (
                                <span className="text-color-tertiary text-sm">—</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge color={user.mfaEnabled ? 'green' : 'orange'}>
                              {user.mfaEnabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-color-secondary">
                              {user.lastLogin
                                ? new Date(user.lastLogin).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : '—'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge
                              color={
                                user.status === 'active'
                                  ? 'green'
                                  : user.status === 'pending'
                                  ? 'orange'
                                  : 'red'
                              }
                            >
                              {capitalizeFirst(user.status)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {filteredUsers.length > 0 && (
                    <div className="mt-4">
                      <Pagination
                        currentPage={usersPage}
                        totalItems={filteredUsers.length}
                        totalPages={usersTotalPages}
                        pageSize={usersPageSize}
                        pageSizeOptions={[10, 25, 50]}
                        showPageInfo
                        showPageSizeList
                        pageSizeListLabel="Users per page"
                        onPageChange={(page) => setUsersPage(page)}
                        onPageSizeChange={(size) => {
                          setUsersPageSize(size);
                          setUsersPage(1);
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Filter Panel */}
          {filterOpen && (
            <Filter
              title="Filter Users"
              container="modal"
              onApply={handleFilterChange}
              onCancel={() => setFilterOpen(false)}
              onClearAll={handleFilterClearAll}
            >
              <FilterGroup
                id="user-status"
                label="Status"
                type="checkbox"
                selected={userStatusFilter}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'pending', label: 'Pending' },
                ]}
              />
              <FilterGroup
                id="user-groups"
                label="Groups"
                type="checkbox"
                selected={userGroupFilter}
                options={groups.map((g) => ({ value: g.id, label: g.name }))}
              />
              <FilterGroup
                id="user-mfa"
                label="MFA Status"
                type="checkbox"
                selected={userMfaFilter}
                options={[
                  { value: 'enabled', label: 'MFA Enabled' },
                  { value: 'disabled', label: 'MFA Disabled' },
                ]}
              />
              <FilterGroup
                id="user-type"
                label="User Type"
                type="checkbox"
                selected={userTypeFilter}
                options={[
                  { value: 'internal', label: 'Internal' },
                  { value: 'external', label: 'External' },
                ]}
              />
            </Filter>
          )}
        </div>
      )}

      {activeTab === 'groups' && (
        <div className="flex flex-col gap-4 mt-4">
          {/* Filters and Actions */}
          <div className="flex items-center justify-between gap-3">
            <div className="w-full max-w-xs">
              <Search
                id="group-search"
                placeholder="Search groups..."
                onChange={(e) => {
                  setGroupSearch(e.target.value);
                  setGroupsPage(1);
                }}
              />
            </div>
            <Button
              variant="primary"
              icon={<PlusIcon />}
              onClick={() => setShowCreateGroupDialog(true)}
            >
              Create Group
            </Button>
          </div>

          {/* Groups Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Groups List */}
            <Card>
              <CardContent>
                <h3 className="text-sm font-semibold mb-3">
                  Groups ({filteredGroups.length})
                </h3>
                {filteredGroups.length === 0 ? (
                  <EmptyState icon={<GroupIcon />} description="No groups found" />
                ) : (
                  <>
                    <div className="-mx-2 divide-y divide-gray-200 dark:divide-gray-800">
                      {paginatedGroups.map((group) => {
                        const count = memberCountByGroup[group.id] ?? 0;
                        const isSelected = selectedGroupId === group.id;
                        return (
                          <div
                            key={group.id}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isSelected}
                            aria-current={isSelected ? 'true' : undefined}
                            className={`group/item flex items-center gap-3 px-2 py-3 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                              isSelected
                                ? 'bg-gray-100 dark:bg-gray-800/60'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
                            }`}
                            onClick={() => {
                              setSelectedGroupId(group.id);
                              setMembersPage(1);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedGroupId(group.id);
                                setMembersPage(1);
                              }
                            }}
                          >
                            <Avatar
                              text={group.name.slice(0, 2).toUpperCase()}
                              size="sm"
                            />
                            <div className="flex-1 min-w-0">
                              <p
                                className={`truncate ${
                                  isSelected ? 'font-semibold' : 'font-medium'
                                }`}
                              >
                                {group.name}
                              </p>
                              <p className="text-xs text-color-tertiary mt-0.5">
                                {count} member{count !== 1 ? 's' : ''}
                              </p>
                            </div>
                            {isSelected ? (
                              <ChevronRightIcon className="h-4 w-4 text-color-tertiary" />
                            ) : (
                              <Badge color="gray">{String(count)}</Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {groupsTotalPages > 1 && (
                      <div className="mt-3 flex justify-center">
                        <Pagination
                          currentPage={groupsPage}
                          totalItems={filteredGroups.length}
                          totalPages={groupsTotalPages}
                          pageSize={groupsPageSize}
                          onPageChange={setGroupsPage}
                        />
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Group Details */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent>
                  {selectedGroup ? (
                    <>
                      <div className="mb-4 flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar
                            text={selectedGroup.name.slice(0, 2).toUpperCase()}
                            size="md"
                          />
                          <div className="min-w-0">
                            <h3 className="text-lg font-semibold">{selectedGroup.name}</h3>
                            <p className="text-sm text-color-tertiary">
                              {selectedGroupMembers.length} member
                              {selectedGroupMembers.length !== 1 ? 's' : ''}
                            </p>
                            {selectedGroup.description && (
                              <p className="text-sm text-color-secondary dark:text-color-secondary-dark mt-1">
                                {selectedGroup.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="primary"
                            icon={<PlusIcon />}
                            onClick={() => setShowAddMemberDialog(true)}
                          >
                            Add Member
                          </Button>
                          <Button
                            ref={groupActionsRef}
                            variant="tertiary"
                            aria-label="Group actions"
                            icon={<MoreVerticalIcon />}
                          />
                          <ContextualMenu
                            anchor={groupActionsRef}
                            position="bottom-right"
                          >
                            <ContextualMenuItem
                              as="button"
                              id="edit-group"
                              label="Edit details"
                              prefix={<EditIcon />}
                              onClick={() => {
                                setGroupNameDraft(selectedGroup.name);
                                setGroupDescDraft(selectedGroup.description ?? '');
                                setEditingGroup(true);
                              }}
                            />
                            <Divider />
                            <ContextualMenuItem
                              as="button"
                              id="delete-group"
                              label="Delete group"
                              prefix={<TrashIcon />}
                              onClick={() => setShowDeleteGroupConfirm(true)}
                            />
                          </ContextualMenu>
                        </div>
                      </div>

                      <h4 className="mb-3 text-sm font-semibold">
                        Members ({selectedGroupMembers.length})
                      </h4>

                      {selectedGroupMembers.length === 0 ? (
                        <EmptyState
                          icon={<GroupIcon />}
                          description="No members yet. Use “Add Member” to add users to this group."
                        />
                      ) : (
                        <>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginatedMembers.map((member) => (
                                <TableRow key={member.userId}>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Avatar
                                        text={getInitials(member.firstName, member.lastName, member.email)}
                                        size="sm"
                                        aria-label={`${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.email || 'member'}
                                        style={getAvatarColor(member.userId || member.email || '')}
                                      />
                                      <div>
                                        <p className="text-sm font-medium">
                                          {toTitleCase(member.firstName)} {toTitleCase(member.lastName)}
                                        </p>
                                        <p className="text-xs text-color-tertiary">
                                          {member.email}
                                        </p>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      color={member.userType === 'internal' ? 'blue' : 'gray'}
                                    >
                                      {capitalizeFirst(member.userType)}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="destructive-secondary"
                                      onClick={() => handleRemoveMemberFromGroup(member.userId)}
                                    >
                                      Remove
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          {membersTotalPages > 1 && (
                            <div className="mt-3 flex justify-center">
                              <Pagination
                                currentPage={effectiveMembersPage}
                                totalItems={selectedGroupMembers.length}
                                totalPages={membersTotalPages}
                                pageSize={membersPageSize}
                                onPageChange={setMembersPage}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <EmptyState
                      icon={<GroupIcon />}
                      description="Select a group to view details"
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'requests' && (
        <div className="mt-4">
          <JoinRequestsTab onDecided={() => pendingRequestsQuery.reload()} />
        </div>
      )}

      {/* User Detail Panel */}
      {selectedUser && (
        <UserDetailPanel
          user={{
            ...selectedUser,
            groups:
              groupIdsByUser[selectedUser.id]?.length
                ? groupIdsByUser[selectedUser.id]
                : selectedUser.groups,
          }}
          allGroups={groups.map((g) => ({ id: g.id, name: g.name }))}
          onClose={() => setSelectedUser(undefined)}
          onRemoveGroup={handleRemoveGroup}
          onSuspendUser={handleSuspendUser}
        />
      )}

      {/* Invite User Dialog */}
      {showInviteDialog && (
        <InviteUserDialog
          availableGroups={groups.map((g) => ({ id: g.id, name: g.name }))}
          onClose={() => setShowInviteDialog(false)}
          onSuccess={handleInviteSuccess}
        />
      )}

      {/* Create Group Dialog */}
      {showCreateGroupDialog && (
        <CreateGroupDialog
          onClose={() => setShowCreateGroupDialog(false)}
          onSuccess={handleCreateGroupSuccess}
        />
      )}

      {/* Edit Group Dialog */}
      {editingGroup && selectedGroup && (
        <Modal
          title="Edit Group"
          onModalClose={() => setEditingGroup(false)}
          onBackdropClick={() => setEditingGroup(false)}
          footerLeft={
            <Button variant="tertiary" onClick={() => setEditingGroup(false)}>
              Cancel
            </Button>
          }
          footerRight={
            <Button
              variant="primary"
              onClick={handleEditGroup}
              disabled={!groupNameDraft.trim() || isEditingGroup}
            >
              {isEditingGroup ? 'Saving...' : 'Save'}
            </Button>
          }
        >
          <ModalContent padded>
            <div className="flex flex-col gap-3">
              <Control hideSpacing="bottom">
                <Label htmlFor="edit-group-name">Group Name</Label>
                <Input
                  id="edit-group-name"
                  value={groupNameDraft}
                  onChange={(e) => setGroupNameDraft(e.target.value)}
                  required
                />
              </Control>
              <Control hideSpacing="bottom">
                <Label htmlFor="edit-group-desc">Description (Optional)</Label>
                <TextArea
                  id="edit-group-desc"
                  rows={3}
                  value={groupDescDraft}
                  onChange={(e) => setGroupDescDraft(e.target.value)}
                />
              </Control>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Add Member Dialog */}
      {showAddMemberDialog && selectedGroup && selectedGroupId && (
        <AddMemberDialog
          groupName={selectedGroup.name}
          existingMemberIds={selectedGroupMembers.map((m) => m.userId)}
          isSubmitting={isAddingMember}
          onAdd={handleAddMembers}
          onClose={() => setShowAddMemberDialog(false)}
        />
      )}

      {/* Delete Group Confirm */}
      {showDeleteGroupConfirm && selectedGroup && (
        <Modal
          title="Delete Group"
          onModalClose={() => setShowDeleteGroupConfirm(false)}
          onBackdropClick={() => setShowDeleteGroupConfirm(false)}
          footerLeft={
            <Button variant="tertiary" onClick={() => setShowDeleteGroupConfirm(false)}>
              Cancel
            </Button>
          }
          footerRight={
            <Button
              variant="destructive-secondary"
              icon={<TrashIcon />}
              onClick={handleDeleteGroup}
              disabled={isDeletingGroup}
            >
              {isDeletingGroup ? 'Deleting...' : 'Delete Group'}
            </Button>
          }
        >
          <ModalContent padded>
            <p className="text-sm">
              Delete <strong>{selectedGroup.name}</strong>? Members will lose this
              group assignment. This cannot be undone.
            </p>
          </ModalContent>
        </Modal>
      )}
      </>
      )}
    </div>
  );
};

export default AccessManagementPage;
